import type { CSSProperties } from 'react';
import { useState } from 'react';
import { useRouter } from '@/lib/navigation';
import { useSession } from '@/contexts/SessionContext';
import { visibleSectionsFor, FRONTENDS, FRONTEND_KEYS } from '@/lib/frontends';
import type { SidebarSection } from '@/lib/frontends';
import { isActivePersona, type FrontendKey } from '@septima/contract';
// Direct component imports — not the `@/components/ui` barrel — so the
// always-mounted shell does not drag charting (Sparkline) and other heavy
// barrel exports into every staff route (PRD #156).
import Sidebar from '@/components/ui/Sidebar';
import SidebarRow from '@/components/ui/SidebarRow';
import AgentBadge from '@/components/agent/AgentBadge';
import SignOutButton from '@/components/ui/SignOutButton';
import Image from '@/lib/Image';
import { getSeptimaMarkPath } from '@/lib/logos';
import { useTheme } from '@/lib/ThemeContext';
import { useDialog } from '@/lib/useDialog';
import { useReviewQueueCounts } from '@/lib/use-review-queue-counts';
import { cn, initialsOf } from '@/lib/utils';
import {
    BookOpen,
    PieChart,
    CheckSquare,
    Building2,
    Phone,
    Shield,
    Users,
    FileText,
    TrendingUp,
    Settings,
    LayoutDashboard,
    Scale,
    ArrowRightLeft,
    Wallet,
    Receipt,
    Plug,
    FileBarChart,
    Heart,
    ClipboardCheck,
    UserCheck,
    AlertTriangle,
    GraduationCap,
    FileWarning,
    Calendar,
    Landmark,
    Building,
    LineChart,
    Activity,
    UsersRound,
    Rocket,
    AlertOctagon,
    BarChart3,
    Presentation,
    Circle,
    Sparkles,
    ShieldCheck,
    BrainCircuit,
    RotateCcw,
    Workflow,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useToast } from '@/lib/useToast';

interface AppSidebarProps {
    frontend: FrontendKey;
    mobileOpen?: boolean;
    onMobileClose?: () => void;
}

const frontendDescriptions: Record<FrontendKey, string> = {
    desk: 'Advisor workspace',
    ops: 'Operations control',
    comply: 'Regulatory command',
    view: 'Executive intelligence',
};

// Presentation-only grouping of the sidebar rows into labelled sections, keyed
// by section `key`. `groupSections` resolves keys against the persona-visible
// section list (so a key the active persona cannot see, or that this frontend
// does not have, is silently dropped) and sweeps any leftover row into "More",
// so this map can never make a row disappear.
const sidebarGroups: Record<FrontendKey, Array<{ label: string; keys: string[] }>> = {
    desk: [
        {
            label: 'Book',
            keys: ['my-book', 'portfolio', 'client-book', 'client-knowledge', 'overdrawn-cash'],
        },
        {
            label: 'Client work',
            keys: ['onboarding', 'actions', 'corporate-actions', 'calls-meetings', 'fact-find'],
        },
        { label: 'Advice controls', keys: ['suitability', 'desk-vulnerability'] },
        { label: 'Content and AI', keys: ['my-page', 'agents'] },
        { label: 'System', keys: ['settings'] },
    ],
    ops: [
        { label: 'Command', keys: ['command-centre', 'agents'] },
        {
            label: 'Processing',
            keys: [
                'reconciliation',
                'corporate-actions',
                'trade-operations',
                'cash-management',
                'overdrawn-cash',
            ],
        },
        { label: 'Client operations', keys: ['onboarding', 'client-book', 'document-vault'] },
        { label: 'Reporting', keys: ['reporting', 'fees-billing'] },
        { label: 'System', keys: ['settings'] },
    ],
    comply: [
        { label: 'Command', keys: ['dashboard', 'my-page', 'agents'] },
        {
            label: 'Regulatory controls',
            keys: [
                'consumer-duty',
                'cmp-reviews',
                'smcr-directory',
                'suitability',
                'fin-crime-kyc',
                'overdrawn-escalations',
            ],
        },
        {
            label: 'Conduct intelligence',
            keys: [
                'onboarding',
                'vulnerability',
                'fair-value',
                'data-freshness',
                'tc-tracker',
                'breach-register',
                'atlas-intelligence',
            ],
        },
        { label: 'Reporting', keys: ['reg-capital'] },
        { label: 'System', keys: ['settings'] },
    ],
    view: [
        {
            label: 'Executive',
            keys: ['firm-overview', 'financial-performance', 'investment-performance', 'kpi'],
        },
        {
            label: 'Intelligence',
            keys: [
                'operational-intelligence',
                'client-intelligence',
                'custodian-analytics',
                'business-development',
                'benchmarking',
            ],
        },
        { label: 'Governance', keys: ['risk-register'] },
        {
            label: 'Platform',
            keys: [
                'agent-activity-log',
                'data-architecture',
                'agents',
                'administration',
                'settings',
            ],
        },
    ],
};

// Icon mapping from name to component
const iconMap: Record<string, LucideIcon> = {
    BookOpen,
    PieChart,
    CheckSquare,
    Building2,
    Phone,
    Shield,
    Users,
    FileText,
    TrendingUp,
    Settings,
    LayoutDashboard,
    Scale,
    ArrowRightLeft,
    Wallet,
    Receipt,
    Plug,
    FileBarChart,
    Heart,
    ClipboardCheck,
    UserCheck,
    AlertTriangle,
    GraduationCap,
    FileWarning,
    Calendar,
    Landmark,
    Building,
    LineChart,
    Activity,
    UsersRound,
    Rocket,
    AlertOctagon,
    BarChart3,
    Presentation,
    Circle,
    Sparkles,
    ShieldCheck,
    BrainCircuit,
    Workflow,
};

// Helper to get icon component from name
function getIcon(name: string): LucideIcon {
    return iconMap[name] || Circle;
}

// Fold the persona-visible sections into their labelled groups, preserving the
// group order in `sidebarGroups`. Any visible section not claimed by a group is
// appended under "More" so a missing key can never hide a row.
function groupSections(frontend: FrontendKey, sections: SidebarSection[]) {
    const byKey = new Map(sections.map((section) => [section.key, section]));
    const used = new Set<string>();
    const groups = sidebarGroups[frontend]
        .map((group) => {
            const items = group.keys
                .map((key) => byKey.get(key))
                .filter((section): section is SidebarSection => Boolean(section));
            items.forEach((item) => used.add(item.key));
            return { label: group.label, items };
        })
        .filter((group) => group.items.length > 0);

    const remaining = sections.filter((section) => !used.has(section.key));
    if (remaining.length > 0) {
        groups.push({ label: 'More', items: remaining });
    }
    return groups;
}

export default function AppSidebar({ frontend, mobileOpen, onMobileClose }: AppSidebarProps) {
    const router = useRouter();
    useDialog({ isOpen: !!mobileOpen, onClose: onMobileClose ?? (() => {}) });
    const { user, grantedFrontends } = useSession();
    const { resolvedTheme } = useTheme();
    const { pushToast } = useToast();
    const [resetting, setResetting] = useState(false);
    // The sidebar rows are decided by the ACTIVE persona (ADR-0013): on the Desk
    // shell that may be `desk` or `im-assistant` (the latter omits the
    // suitability + vulnerability rows), and the View → Administration row is
    // revealed from the granted tokens. Fall back to the frontend prop when the
    // session carries no (or a stale) active persona.
    const activePersona =
        user?.activePersona && isActivePersona(user.activePersona) ? user.activePersona : frontend;
    const sections = visibleSectionsFor(activePersona, user?.personas ?? []);
    const groupedSections = groupSections(frontend, sections);

    // Per-frontend accent alias so the active switcher button tints with the
    // persona colour through one token rather than four conditional branches.
    const sidebarStyle = {
        '--sidebar-accent': `var(--color-persona-${frontend})`,
    } as CSSProperties;

    // The 'Agents' sidebar row carries the unified pending-review count —
    // AgentTask review_required + persisted artefact rows pending review —
    // since review now happens on the Agents Kanban's "Needs Review" column.
    // Backed by the lightweight counts endpoint (not the full queue model)
    // so the badge does not load the entire pending payload into chrome.
    const { itemCount: reviewItemCount } = useReviewQueueCounts(frontend);
    const reviewBadge = reviewItemCount > 0 ? reviewItemCount : undefined;

    // Persona switch (ADR-0004 / issue #100): navigate to the new frontend
    // and let `FrontendLayout`'s sync effect call `/api/auth/persona`. We
    // route the audit + cookie update through that single point so a
    // deep-link entry (typing /comply) and a sidebar click share the
    // exact same code path — and the cookie is never out of step with
    // the visible URL.
    const handleSwitchFrontend = (newFrontend: FrontendKey) => {
        router.push(`/${newFrontend}`);
    };

    async function resetDemo() {
        if (resetting) return;
        setResetting(true);
        try {
            const res = await fetch('/api/demo/reset', {
                method: 'POST',
                credentials: 'include',
            });
            if (!res.ok) throw new Error(`reset ${res.status}`);
            pushToast({ kind: 'success', message: 'Demo data reset' });
            window.location.reload();
        } catch {
            pushToast({ kind: 'error', message: 'Could not reset demo data' });
            setResetting(false);
        }
    }

    return (
        <Sidebar
            mobileOpen={mobileOpen}
            style={sidebarStyle}
            brandContent={
                <div className="flex flex-col gap-2.5">
                    <div className="flex items-center gap-2.5 px-1">
                        <div className="grid h-9 w-9 flex-none place-items-center rounded-[9px] border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))]">
                            <Image
                                src={getSeptimaMarkPath(resolvedTheme)}
                                alt="Septima"
                                width={21}
                                height={21}
                            />
                        </div>
                        <div className="min-w-0">
                            <div className="truncate text-[13.5px] font-semibold leading-5 text-[rgb(var(--color-text-primary))]">
                                {FRONTENDS[frontend].label}
                            </div>
                            <div className="truncate text-[11.5px] leading-4 text-[rgb(var(--color-text-secondary))]">
                                {frontendDescriptions[frontend]}
                            </div>
                        </div>
                    </div>

                    {/* View switcher — only the user's granted views (ADR-0004). */}
                    {grantedFrontends.length > 1 && (
                        <div className="flex gap-1 px-1">
                            {FRONTEND_KEYS.filter((f) => grantedFrontends.includes(f)).map((f) => (
                                <button
                                    key={f}
                                    type="button"
                                    aria-label={`Switch to ${FRONTENDS[f].label}`}
                                    title={`Switch to ${FRONTENDS[f].label}`}
                                    onClick={() => handleSwitchFrontend(f)}
                                    className={cn(
                                        'h-7 flex-1 rounded-[7px] text-[11.5px] font-medium transition-colors',
                                        f === frontend
                                            ? 'bg-[rgb(var(--sidebar-accent))] text-white'
                                            : 'bg-[rgb(var(--color-text-primary)/0.04)] text-[rgb(var(--color-text-secondary))] hover:bg-[rgb(var(--color-text-primary)/0.08)] hover:text-[rgb(var(--color-text-primary))]',
                                    )}
                                >
                                    {FRONTENDS[f].shortLabel}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            }
            userCard={
                <div className="flex flex-col gap-2">
                    <AgentBadge frontend={frontend} />
                    {user && (
                        <div className="flex items-center gap-2.5 rounded-[10px] border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))] px-2.5 py-2">
                            <div
                                className={cn(
                                    'w-8 h-8 rounded-[8px] flex items-center justify-center',
                                    'text-white font-medium text-annotation',
                                    frontend === 'desk' && 'bg-[rgb(var(--color-persona-desk))]',
                                    frontend === 'ops' && 'bg-[rgb(var(--color-persona-ops))]',
                                    frontend === 'comply' &&
                                        'bg-[rgb(var(--color-persona-comply))]',
                                    frontend === 'view' && 'bg-[rgb(var(--color-persona-view))]',
                                )}
                            >
                                {initialsOf(user.name)}
                            </div>
                            <div className="flex flex-col min-w-0">
                                <span className="text-body-medium text-[rgb(var(--color-text-primary))] truncate">
                                    {user.name ?? 'Signed in'}
                                </span>
                                {user.email && (
                                    <span className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                                        {user.email}
                                    </span>
                                )}
                            </div>
                        </div>
                    )}
                    <div className="border-t border-[rgb(var(--color-divider))] pt-1">
                        <button
                            type="button"
                            onClick={() => void resetDemo()}
                            disabled={resetting}
                            aria-busy={resetting || undefined}
                            className={cn(
                                'mb-1 w-full flex items-center gap-2 px-2.5 py-2 rounded-[9px]',
                                'text-annotation text-[rgb(var(--color-text-tertiary))]',
                                'hover:text-[rgb(var(--color-text-secondary))] hover:bg-[rgb(var(--color-text-primary)/0.05)]',
                                'transition-colors',
                                'disabled:opacity-50 disabled:cursor-wait',
                            )}
                        >
                            <RotateCcw
                                className={cn(
                                    'w-3.5 h-3.5 flex-shrink-0',
                                    resetting && 'animate-spin',
                                )}
                            />
                            {resetting ? 'Resetting demo...' : 'Reset Demo'}
                        </button>
                        <SignOutButton />
                    </div>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                {groupedSections.map((group) => (
                    <section key={group.label} aria-label={group.label}>
                        <div className="mb-1.5 px-2 text-[11px] font-medium leading-4 text-[rgb(var(--color-text-tertiary))]">
                            {group.label}
                        </div>
                        <div className="flex flex-col gap-0.5">
                            {group.items.map((section) => {
                                const badge =
                                    section.key === 'agents' ? reviewBadge : section.badge;
                                return (
                                    <SidebarRow
                                        key={section.key}
                                        icon={getIcon(section.icon)}
                                        label={section.label}
                                        href={section.href}
                                        badge={badge}
                                        aliases={section.aliases}
                                    />
                                );
                            })}
                        </div>
                    </section>
                ))}
            </div>
        </Sidebar>
    );
}
